import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../../services/api';
import styles from './OpenAIPodcastTool.module.css';

const DEFAULT_MODEL = 'gpt-5-nano';
const MODE_UPLOAD = 'upload';
const MODE_BATCH = 'batch';
const BATCH_VARIANT_COMPLEX = 'complex';
const BATCH_VARIANT_SIMPLE = 'simple';

function OpenAIPodcastTool() {
  const [selectedMode, setSelectedMode] = useState(MODE_UPLOAD);
  const [selectedFile, setSelectedFile] = useState(null);
  const [model, setModel] = useState(DEFAULT_MODEL);
  const [instructions, setInstructions] = useState('');
  const [result, setResult] = useState(null);
  const [batchResult, setBatchResult] = useState(null);
  const [error, setError] = useState('');
  const [copyStatus, setCopyStatus] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [progressLogs, setProgressLogs] = useState([]);
  const [statusMessage, setStatusMessage] = useState('');
  const [volumes, setVolumes] = useState([]);
  const [volumesLoading, setVolumesLoading] = useState(false);
  const [volumesError, setVolumesError] = useState('');
  const [selectedVolumeId, setSelectedVolumeId] = useState('');
  const [selectedChapterIds, setSelectedChapterIds] = useState([]);
  const [batchVariant, setBatchVariant] = useState(BATCH_VARIANT_COMPLEX);

  const backendBaseUrl = useMemo(() => {
    const baseUrl = api.defaults.baseURL || '';
    if (!baseUrl) {
      return '';
    }
    const trimmed = baseUrl.replace(/\/?$/, '');
    return trimmed.replace(/\/api$/, '');
  }, []);

  const openDocsUrl = backendBaseUrl
    ? `${backendBaseUrl}/docs#/podcast-generation/generate_podcast_script_podcast_generation__post`
    : '/docs';

  const resolveStaticUrl = useCallback(
    (url) => {
      if (!url) {
        return '';
      }
      if (/^https?:\/\//i.test(url)) {
        return url;
      }
      return backendBaseUrl ? `${backendBaseUrl}${url}` : url;
    },
    [backendBaseUrl],
  );

  const loadVolumes = useCallback(async () => {
    setVolumesLoading(true);
    setVolumesError('');
    try {
      const { data } = await api.get('/volumes/', { params: { limit: 500 } });
      const list = Array.isArray(data) ? data : [];
      setVolumes(list);
      setSelectedVolumeId((prev) => {
        if (prev && list.some((item) => String(item.id) === String(prev))) {
          return prev;
        }
        return list.length > 0 ? String(list[0].id) : '';
      });
    } catch (err) {
      const message =
        err?.response?.data?.detail ||
        err?.message ||
        '获取卷列表失败，请稍后重试';
      setVolumesError(message);
    } finally {
      setVolumesLoading(false);
    }
  }, []);

  const chaptersInVolume = useMemo(() => {
    if (!selectedVolumeId) {
      return [];
    }
    const volume = volumes.find((item) => String(item.id) === String(selectedVolumeId));
    if (!volume || !Array.isArray(volume.chapters)) {
      return [];
    }
    return [...volume.chapters].sort((a, b) => {
      const left = typeof a.order_index === 'number' ? a.order_index : 0;
      const right = typeof b.order_index === 'number' ? b.order_index : 0;
      return left - right;
    });
  }, [selectedVolumeId, volumes]);

  const allChaptersSelected = useMemo(() => {
    return chaptersInVolume.length > 0 && selectedChapterIds.length === chaptersInVolume.length;
  }, [chaptersInVolume, selectedChapterIds.length]);

  const generatedChapterCount = useMemo(() => {
    return chaptersInVolume.filter((item) => Boolean(item.has_podcast)).length;
  }, [chaptersInVolume]);

  const resetUiState = useCallback(() => {
    setError('');
    setCopyStatus('');
    setStatusMessage('');
    setProgressLogs([]);
  }, []);

  useEffect(() => {
    if (selectedMode !== MODE_BATCH) {
      return;
    }
    if (volumes.length === 0 && !volumesLoading) {
      loadVolumes();
    }
  }, [selectedMode, volumes.length, volumesLoading, loadVolumes]);

  useEffect(() => {
    if (selectedMode === MODE_BATCH) {
      setSelectedChapterIds([]);
    }
  }, [selectedMode, selectedVolumeId]);

  const handleModeChange = (mode) => {
    if (mode === selectedMode) {
      return;
    }
    setSelectedMode(mode);
    resetUiState();
    setResult(null);
    setBatchResult(null);
    if (mode === MODE_UPLOAD) {
      setSelectedFile(null);
      setBatchVariant(BATCH_VARIANT_COMPLEX);
    } else if (mode === MODE_BATCH) {
      setSelectedChapterIds([]);
      setBatchVariant(BATCH_VARIANT_COMPLEX);
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files?.[0] ?? null;
    event.target.value = '';

    if (file && !/\.json$/i.test(file.name)) {
      setSelectedFile(null);
      setResult(null);
      setBatchResult(null);
      setError('文件格式错误，请上传 JSON 文件');
      setCopyStatus('');
      return;
    }

    setSelectedFile(file);
    setResult(null);
    setBatchResult(null);
    setError('');
    setCopyStatus('');
    setProgressLogs([]);
    setStatusMessage('');
    if (file && file.size === 0) {
      setError('文件为空，请选择有效的 JSON 文件');
    }
  };

  const handleChapterToggle = (chapterId) => {
    setSelectedChapterIds((prev) => {
      const id = String(chapterId);
      if (prev.includes(id)) {
        return prev.filter((item) => item !== id);
      }
      return [...prev, id];
    });
  };

  const handleSelectAllChapters = () => {
    if (allChaptersSelected) {
      setSelectedChapterIds([]);
    } else {
      setSelectedChapterIds(chaptersInVolume.map((item) => String(item.id)));
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    resetUiState();
    setResult(null);
    setBatchResult(null);

    if (selectedMode === MODE_UPLOAD) {
      if (!selectedFile) {
        setError('请选择要上传的 JSON 文件');
        return;
      }
      if (selectedFile.size === 0) {
        setError('文件为空，请重新上传 JSON 文件');
        return;
      }

      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('model', model.trim() || DEFAULT_MODEL);
      if (instructions.trim()) {
        formData.append('instructions', instructions.trim());
      }

      try {
        setIsGenerating(true);
        setStatusMessage('正在生成，请稍候...');
        const { data } = await api.post('/podcast-generation/', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 0,
        });
        setResult(data);
        setError('');

        if (typeof data?.batches_processed === 'number' && typeof data?.total_batches === 'number') {
          const progressText = `已处理批次：${data.batches_processed}/${data.total_batches}`;
          setProgressLogs((prev) => [...prev, progressText]);
          console.log(`[OpenAI Podcast] ${progressText}`);
        }

        if (data?.is_partial) {
          const reason = data?.partial_reason || '未知原因';
          setStatusMessage(`生成部分完成：${reason}`);
        } else {
          setStatusMessage('生成完成');
        }
      } catch (err) {
        setStatusMessage('');
        if (err?.code === 'ECONNABORTED') {
          setError('生成失败：请求超时，请稍后重试或精简文本。');
        } else {
          const responseData = err?.response?.data;
          if (
            responseData &&
            typeof responseData?.batches_processed === 'number' &&
            typeof responseData?.total_batches === 'number'
          ) {
            const progressText = `已处理批次：${responseData.batches_processed}/${responseData.total_batches}`;
            setProgressLogs((prev) => [...prev, progressText]);
            console.log(`[OpenAI Podcast] ${progressText}`);
          }

          const detail =
            (typeof responseData?.detail === 'string' && responseData.detail) ||
            (typeof responseData === 'string' && responseData) ||
            err?.message ||
            '生成失败，请稍后重试';
          setError(`生成失败：${detail}`);
        }
      } finally {
        setIsGenerating(false);
      }
      return;
    }

    if (selectedChapterIds.length === 0) {
      setError('请选择至少一个章节');
      return;
    }

    const payload = {
      chapter_ids: selectedChapterIds.map((id) => Number(id)),
      model: model.trim() || DEFAULT_MODEL,
    };
    if (instructions.trim()) {
      payload.instructions = instructions.trim();
    }

    const variantLabelText = batchVariant === BATCH_VARIANT_SIMPLE ? '简易' : '复杂';

    try {
      setIsGenerating(true);
      setStatusMessage(`正在批量生成（${variantLabelText}），请稍候（共 ${selectedChapterIds.length} 个章节）...`);
      const endpoint =
        batchVariant === BATCH_VARIANT_SIMPLE
          ? '/podcast-generation/simple-batch'
          : '/podcast-generation/batch';
      const { data } = await api.post(endpoint, payload, {
        timeout: 0,
      });
      setBatchResult(data);
      setError('');
      const suffix = batchVariant === BATCH_VARIANT_SIMPLE ? '（简易）' : '（复杂）';
      setStatusMessage(`批量生成完成${suffix}`);
    } catch (err) {
      setStatusMessage('');
      const detail =
        err?.response?.data?.detail ||
        err?.message ||
        '批量生成失败，请稍后重试';
      setError(`批量生成失败（${variantLabelText}）：${detail}`);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleCopyText = async () => {
    if (!result?.content || selectedMode !== MODE_UPLOAD) {
      return;
    }

    try {
      if (typeof navigator === 'undefined' || !navigator.clipboard) {
        setCopyStatus('浏览器不支持剪贴板复制');
        return;
      }

      await navigator.clipboard.writeText(result.content);
      setCopyStatus('已复制到剪贴板');
    } catch (err) {
      setCopyStatus('复制失败，请手动复制');
    }
  };

  const handleDownloadText = () => {
    if (!result?.content || selectedMode !== MODE_UPLOAD) {
      return;
    }

    if (
      typeof window === 'undefined' ||
      !window.URL ||
      typeof window.URL.createObjectURL !== 'function'
    ) {
      setCopyStatus('当前环境不支持文件下载');
      return;
    }

    const payload = result.content;
    const blob = new Blob([payload], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const baseName = (selectedFile?.name || 'podcast')
      .replace(/\.(json|txt)$/i, '')
      .replace(/[\\s/\\\\:*?"<>|]+/g, '_')
      .slice(0, 50);
    const fileName = `${baseName || 'podcast'}_script.txt`;

    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div className={styles.podcastTool}>
      <header className={styles.header}>
        <div>
          <h1 className={styles.title}>OpenAI 播客生成</h1>
          <p className={styles.subtitle}>
            上传包含英文句子的 JSON 数据，让一位中文英语老师生成带有中英文双语内容的播客脚本，
            或直接对已翻译的章节批量生成播客脚本。
          </p>
        </div>
        <a
          className={styles.docsLink}
          href={openDocsUrl}
          target="_blank"
          rel="noopener noreferrer"
        >
          接口文档
        </a>
      </header>

      <section className={styles.panel}>
        <form className={styles.form} onSubmit={handleSubmit}>
          <div className={styles.modeSwitch}>
            <button
              type="button"
              className={`${styles.modeButton} ${
                selectedMode === MODE_UPLOAD ? styles.modeButtonActive : ''
              }`}
              onClick={() => handleModeChange(MODE_UPLOAD)}
            >
              上传 JSON
            </button>
            <button
              type="button"
              className={`${styles.modeButton} ${
                selectedMode === MODE_BATCH ? styles.modeButtonActive : ''
              }`}
              onClick={() => handleModeChange(MODE_BATCH)}
            >
              批量生成（按章节）
            </button>
          </div>

          {selectedMode === MODE_BATCH ? (
            <div className={styles.formRow}>
              <p className={styles.hint}>
                选择卷并勾选章节，系统将读取章节的 <code>content_trans</code> 字段作为输入，
                将生成的播客脚本写入章节的 <code>podcast</code> 字段。可在下方选择「简易」或「复杂」模式：
                简易模式会一次性生成约 2000 字以内的播客脚本，复杂模式会分批生成更长的完整节目。
              </p>
            </div>
          ) : null}

          {selectedMode === MODE_BATCH ? (
            <div className={styles.modeSwitch}>
              <button
                type="button"
                className={`${styles.modeButton} ${
                  batchVariant === BATCH_VARIANT_COMPLEX ? styles.modeButtonActive : ''
                }`}
                onClick={() => {
                  if (batchVariant !== BATCH_VARIANT_COMPLEX) {
                    setBatchVariant(BATCH_VARIANT_COMPLEX);
                    setBatchResult(null);
                  }
                }}
              >
                复杂模式
              </button>
              <button
                type="button"
                className={`${styles.modeButton} ${
                  batchVariant === BATCH_VARIANT_SIMPLE ? styles.modeButtonActive : ''
                }`}
                onClick={() => {
                  if (batchVariant !== BATCH_VARIANT_SIMPLE) {
                    setBatchVariant(BATCH_VARIANT_SIMPLE);
                    setBatchResult(null);
                  }
                }}
              >
                简易模式
              </button>
            </div>
          ) : null}

          {selectedMode === MODE_UPLOAD ? (
            <div className={styles.formRow}>
              <label className={styles.label} htmlFor="podcastFile">
                JSON 文件
              </label>
              <input
                id="podcastFile"
                className={styles.fileInput}
                type="file"
                accept=".json,application/json"
                onChange={handleFileChange}
              />
              {selectedFile && (
                <p className={styles.fileInfo}>
                  已选择：{selectedFile.name}（{(selectedFile.size / 1024).toFixed(1)} KB）
                </p>
              )}
              <p className={styles.hint}>
                文件大小请控制在 4 MB 以内，需为 UTF-8 编码的 JSON，并包含 sentences 数组。
              </p>
            </div>
          ) : (
            <>
              <div className={styles.formRow}>
                <label className={styles.label} htmlFor="volumeSelect">
                  选择卷
                </label>
                <div className={styles.inlineFields}>
                  <select
                    id="volumeSelect"
                    className={styles.input}
                    value={selectedVolumeId}
                    onChange={(event) => setSelectedVolumeId(event.target.value)}
                    disabled={volumesLoading || volumes.length === 0}
                  >
                    {volumes.length === 0 ? (
                      <option value="">暂无可用卷</option>
                    ) : (
                      volumes.map((volume) => {
                        const pieces = [];
                        if (volume.novel?.title) {
                          pieces.push(volume.novel.title);
                        }
                        pieces.push(volume.title);
                        return (
                          <option key={volume.id} value={volume.id}>
                            {pieces.join(' / ')}
                          </option>
                        );
                      })
                    )}
                  </select>
                  <button
                    type="button"
                    className={styles.modeButton}
                    onClick={loadVolumes}
                    disabled={volumesLoading}
                  >
                    {volumesLoading ? '加载中...' : '刷新卷列表'}
                  </button>
                </div>
                {volumesError && <div className={styles.error}>{volumesError}</div>}
                {volumesLoading && !volumesError && (
                  <p className={styles.hint}>卷列表加载中，请稍候...</p>
                )}
              </div>

              <div className={styles.formRow}>
                <div className={styles.selectActions}>
                  <span className={styles.selectionInfo}>
                    已选章节：{selectedChapterIds.length} / {chaptersInVolume.length}
                    {' · '}
                    已有播客：{generatedChapterCount}
                  </span>
                  <button
                    type="button"
                    className={styles.modeButton}
                    onClick={handleSelectAllChapters}
                    disabled={chaptersInVolume.length === 0}
                  >
                    {allChaptersSelected ? '取消全选' : '全选本卷章节'}
                  </button>
                </div>
                {chaptersInVolume.length === 0 ? (
                  <p className={styles.hint}>
                    该卷暂时没有可用章节，请确认章节已导入。
                  </p>
                ) : (
                  <div className={styles.chapterList}>
                    {chaptersInVolume.map((chapter) => (
                      <label key={chapter.id} className={styles.chapterItem}>
                        <input
                          type="checkbox"
                          className={styles.chapterCheckbox}
                          checked={selectedChapterIds.includes(String(chapter.id))}
                          onChange={() => handleChapterToggle(chapter.id)}
                        />
                        <span className={styles.chapterMeta}>
                          <span className={styles.chapterTitle}>{chapter.title}</span>
                          <span className={styles.chapterInfo}>
                            <span className={styles.chapterOrder}>
                              #{typeof chapter.order_index === 'number' ? chapter.order_index : '-'}
                            </span>
                            <span
                              className={
                                chapter.has_podcast
                                  ? `${styles.chapterStatus} ${styles.chapterStatusDone}`
                                  : `${styles.chapterStatus} ${styles.chapterStatusPending}`
                              }
                            >
                              {chapter.has_podcast ? '已生成' : '未生成'}
                            </span>
                          </span>
                        </span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}

          <div className={styles.inlineFields}>
            <div className={styles.formRow}>
              <label className={styles.label} htmlFor="model">
                使用模型
              </label>
              <input
                id="model"
                className={styles.input}
                type="text"
                value={model}
                onChange={(event) => setModel(event.target.value)}
              />
              <p className={styles.hint}>
                默认 {DEFAULT_MODEL}，可根据账号权限调整例如 gpt-4o。
              </p>
            </div>
          </div>

          <div className={styles.formRow}>
            <label className={styles.label} htmlFor="instructions">
              额外说明（可选）
            </label>
            <textarea
              id="instructions"
              className={styles.textarea}
              placeholder="例如：强调词汇解释，语气轻快幽默，时长控制在10分钟内等。"
              value={instructions}
              onChange={(event) => setInstructions(event.target.value)}
            />
          </div>

          {error && <div className={styles.error}>{error}</div>}

          <div className={styles.actions}>
            <button
              type="submit"
              className={styles.submitBtn}
              disabled={isGenerating}
            >
              {isGenerating
                ? selectedMode === MODE_BATCH
                  ? '批量生成中...'
                  : '生成中...'
                : selectedMode === MODE_BATCH
                ? '批量生成播客脚本'
                : '上传并生成播客脚本'}
            </button>
          </div>
        </form>
      </section>

      {(statusMessage || progressLogs.length > 0) && (
        <section className={styles.statusSection}>
          {statusMessage && (
            <p className={styles.statusMessage}>{statusMessage}</p>
          )}
          {progressLogs.length > 0 && (
            <ul className={styles.progressList}>
              {progressLogs.map((log, index) => (
                <li key={`${log}-${index}`}>{log}</li>
              ))}
            </ul>
          )}
        </section>
      )}

      {selectedMode === MODE_UPLOAD && result && (
        <section className={styles.resultSection}>
          <div className={styles.resultHeader}>
            <div>
              <h2 className={styles.resultTitle}>
                {result.is_partial ? '生成部分完成' : '生成完成'}
              </h2>
              <p className={styles.resultSummary}>
                使用模型 {result.model || model}
              </p>
            </div>
            <div className={styles.resultActions}>
              <button
                type="button"
                className={styles.secondaryBtn}
                onClick={handleCopyText}
              >
                复制脚本
              </button>
              <button
                type="button"
                className={styles.secondaryBtn}
                onClick={handleDownloadText}
              >
                下载脚本
              </button>
            </div>
          </div>

          {copyStatus && (
            <div className={styles.copyStatus}>{copyStatus}</div>
          )}

          <div className={styles.meta}>
            <div>
              <span className={styles.metaLabel}>存储路径</span>
              <span className={styles.metaValue}>{result.saved_path}</span>
            </div>
            <div>
              <span className={styles.metaLabel}>处理进度</span>
              <span className={styles.metaValue}>
                {result.batches_processed}/{result.total_batches} 批
              </span>
            </div>
            <div>
              <span className={styles.metaLabel}>下载地址</span>
              <a
                className={styles.metaLink}
                href={resolveStaticUrl(result.saved_url)}
                target="_blank"
                rel="noopener noreferrer"
              >
                {resolveStaticUrl(result.saved_url)}
              </a>
            </div>
            {result.is_partial && result.partial_reason && (
              <div className={styles.partialHint}>
                <span className={styles.metaLabel}>中断原因</span>
                <span className={styles.metaValue}>{result.partial_reason}</span>
              </div>
            )}
          </div>

          <div className={styles.preview}>
            <h3 className={styles.previewTitle}>脚本预览</h3>
            <pre className={styles.previewContent}>{result.content}</pre>
          </div>
        </section>
      )}

      {selectedMode === MODE_BATCH && batchResult && (
        <section className={styles.resultSection}>
          <div className={styles.resultHeader}>
            <div>
              <h2 className={styles.resultTitle}>批量生成结果</h2>
              <p className={styles.resultSummary}>
                成功 {batchResult.succeeded}/{batchResult.total_requested} 个章节
              </p>
            </div>
          </div>

          <div className={styles.meta}>
            <div>
              <span className={styles.metaLabel}>模型</span>
              <span className={styles.metaValue}>{batchResult.model}</span>
            </div>
            <div>
              <span className={styles.metaLabel}>处理章节</span>
              <span className={styles.metaValue}>
                {batchResult.processed}/{batchResult.total_requested}
              </span>
            </div>
            <div>
              <span className={styles.metaLabel}>失败章节</span>
              <span className={styles.metaValue}>{batchResult.failed}</span>
            </div>
          </div>

          {Array.isArray(batchResult.results) && batchResult.results.length > 0 && (
            <div className={styles.tableWrapper}>
              <table className={styles.resultTable}>
                <thead>
                  <tr>
                    <th>章节ID</th>
                    <th>标题</th>
                    <th>状态</th>
                    <th>批次</th>
                    <th>字数估计</th>
                    <th>详情</th>
                    <th>下载</th>
                  </tr>
                </thead>
                <tbody>
                  {batchResult.results.map((item) => {
                    const statusClass =
                      item.status === 'success'
                        ? styles.statusSuccess
                        : item.status === 'partial'
                        ? styles.statusPartial
                        : styles.statusFailed;
                    const batchesText =
                      typeof item.batches_processed === 'number' && typeof item.total_batches === 'number'
                        ? `${item.batches_processed}/${item.total_batches}`
                        : '-';
                    const downloadUrl = resolveStaticUrl(item.saved_url);
                    return (
                      <tr key={`${item.chapter_id}`}>
                        <td>{item.chapter_id}</td>
                        <td>{item.title || '-'}</td>
                        <td className={statusClass}>{item.status}</td>
                        <td>{batchesText}</td>
                        <td>{typeof item.word_count === 'number' ? item.word_count : '-'}</td>
                        <td>{item.detail || item.partial_reason || '-'}</td>
                        <td>
                          {item.saved_url ? (
                            <a
                              href={downloadUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className={styles.metaLink}
                            >
                              下载
                            </a>
                          ) : (
                            '-'
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}
    </div>
  );
}

export default OpenAIPodcastTool;
